import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { ClientOnly } from 'remix-utils/client-only';

import BinIcon from '~/assets/icons/Bin';
import ChevronIcon from '~/assets/icons/Chevron';
import PadLockIcon from '~/assets/icons/Padlock';
import ProfileIcon from '~/assets/icons/Profile';
import { Button, InternalLink } from '~/components/Button';
import Link from '~/components/Link';
import Modals from '~/components/Modals';
import PageHeader from '~/components/PageHeader';
import useRegion from '~/hooks/useRegion';
import useUser from '~/hooks/useUser';
import useViewEvent from '~/hooks/useViewEvent';

import ProfilePageContents from './ProfilePageContents';

export const handle = { i18n: ['profile'] };

export const meta = () => {
  return [{ title: 'My Account' }, { content: 'noindex', name: 'robots' }];
};

function LogOutButton() {
  const { t } = useTranslation('profile');

  return (
    <form
      method="POST"
      action="/bolt/logout"
      className="flex w-full text-[14px] font-bold"
    >
      <Button
        type="submit"
        variant="red-alert-transparent"
        className="flex w-full items-center justify-center gap-2"
      >
        <ProfileIcon color="#D71225" height="16" width="16" />
        {t('signOut')}
      </Button>
    </form>
  );
}
function ChangePasswordButton() {
  const { profile } = useUser();

  const { t } = useTranslation('profile');

  if (profile?.login_type !== 'EMAIL') return;

  return (
    <div className="flex w-full text-[14px] font-bold">
      <Link
        to="/change-password/"
        external
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-black bg-white px-4 py-2 text-center font-semibold text-black transition duration-200 ease-in-out active:scale-95"
      >
        <PadLockIcon color="black" height="16" width="16" />
        {t('changePassword')}
      </Link>
    </div>
  );
}

function DeleteAccountButton() {
  const { t } = useTranslation('profile');

  return (
    <div className="flex w-full text-[14px]">
      <InternalLink
        to="/profile/delete/"
        variant="transparent-grey-bold"
        className="flex w-full items-center justify-center gap-2"
      >
        <BinIcon stroke="#A9A9A9" height={16} width={16} />
        {t('deleteAccount')}
      </InternalLink>
    </div>
  );
}

export default function Profile() {
  useViewEvent({ viewType: 'profile' });

  const user = useUser();
  const [searchParams] = useSearchParams();

  const isConfirmingRegistration = searchParams.has('fromRegistration');

  return (
    <div className="relative bg-white">
      {user.isLoggedIn ? (
        <ProfileContents />
      ) : (
        <div>
          {!isConfirmingRegistration && (
            <div className="fixed z-10 flex size-full items-center justify-center backdrop-blur-sm">
              <ClientOnly>
                {() => (
                  <Modals.SignUp
                    modalType="profile"
                    registrationEntry={{ placement: 'profile_page' }}
                    onDismiss={() => {}}
                    postAuthOptions={{
                      action: 'redirect'
                    }}
                  />
                )}
              </ClientOnly>
            </div>
          )}
          <ProfileContents />
        </div>
      )}
    </div>
  );
}

function ProfileContents() {
  const { profile } = useUser();
  const region = useRegion();
  const { t } = useTranslation('profile');

  return (
    <>
      <PageHeader
        actions={
          <span className="font-heading text-grey-hide max-md:hidden">
            Hi, sole mate!
          </span>
        }
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: t('myAccount'), path: '/profile/' }
        ]}
        title={t('myAccount')}
      />
      <div className="content-width flex flex-col pt-4 max-md:px-0">
        {/* Desktop layout */}
        <div className="hidden md:flex">
          <div className="flex min-w-full flex-col gap-6">
            {ProfilePageContents({ profile, region }).sections.map(
              (section, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-4 rounded-xl bg-grey-panel-dark p-6 shadow"
                >
                  <h2 className="text-title-md font-semibold">
                    {section.title}
                  </h2>
                  <div className="grid grid-cols-2 gap-6 ">
                    {section.items.map((item, index) => (
                      <Link
                        key={index}
                        to={item.path}
                        external={item.external}
                        className="justify-start rounded-lg bg-white p-6 shadow transition-colors duration-300 ease-in-out hover:shadow-lg hover:transition"
                      >
                        <div className="flex flex-col gap-4 leading-snug">
                          <div className="flex flex-row justify-start gap-4">
                            <div className="py-1">{item.icon}</div>
                            {item.title}
                          </div>
                          <div className="h-11 text-grey-midnight-read">
                            {item.description}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Mobile layout */}
        <div className="flex md:hidden">
          <div className="flex grow flex-col bg-grey-panel pt-4">
            {ProfilePageContents({ profile, region }).sections.map(
              (section, index) => (
                <div
                  key={index}
                  className="mb-4 flex flex-col gap-4 bg-white px-6 py-4"
                >
                  <div className="text-start text-base font-bold tracking-wide">
                    {section.title}
                  </div>
                  <div className="flex flex-col">
                    {section.items.map((item, index) => (
                      <div
                        key={index}
                        className="border-b border-grey-panel last:border-none"
                      >
                        <Link to={item.path} external={item.external}>
                          <div className="flex gap-4 py-4">
                            <div className="py-1">{item.icon}</div>
                            <div className="grow">{item.title}</div>
                            <div className="-mr-2 rotate-270 px-1.5">
                              <ChevronIcon
                                width={'12'}
                                height={'12'}
                                stroke="black"
                              />
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
        <div className="flex flex-col gap-6 px-4 py-6 md:flex-row md:px-0">
          <ChangePasswordButton />
          <LogOutButton />
          <DeleteAccountButton />
        </div>
      </div>
    </>
  );
}

export { loader } from './loader.server';
